import { CardLastMessageTimedelta, CardTitle } from "../common";
import React, { useImperativeHandle } from "react";
import {
  faBug,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LogLevelIndicator from "../../helpers/LogLevelIndicator";
import PropTypes from "prop-types";
import { countLogLevels } from "../../helpers/utils";

const LogCard = React.forwardRef(({ card, topic, messages, w, h }, ref) => {
  const action = (setSelectedCard) => {
    setSelectedCard(card);
  };

  useImperativeHandle(ref, () => ({ action }));

  const safeMessages = messages || [];
  const { ERROR, WARNING, INFO, DEBUG } = countLogLevels(safeMessages);
  const lastMessage =
    safeMessages.length > 0 ? safeMessages[0].content : "No logs yet";

  const sizeKey = `${w}x${h}`;

  const renderContent = () => {
    switch (sizeKey) {
      case "1x1":
        return (
          <div className="flex items-center justify-center w-full h-full">
            {ERROR > 0 ? (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-rose-500 text-4xl"
              />
            ) : WARNING > 0 ? (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-amber-500 text-4xl"
              />
            ) : INFO > 0 ? (
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-sky-500 text-4xl"
              />
            ) : (
              <FontAwesomeIcon
                icon={faBug}
                className="text-emerald-500 text-4xl"
              />
            )}
          </div>
        );
      case "1x2":
        return (
          <div className="flex items-center justify-center w-full h-full">
            <div className="flex flex-col items-start justify-around w-max h-full">
              <LogLevelIndicator
                level="ERROR"
                count={ERROR}
                label="Error"
                labelOnSide
              />
              <LogLevelIndicator
                level="WARNING"
                count={WARNING}
                label="Warn"
                labelOnSide
              />
              <LogLevelIndicator
                level="INFO"
                count={INFO}
                label="Info"
                labelOnSide
              />
              <LogLevelIndicator
                level="DEBUG"
                count={DEBUG}
                label="Debug"
                labelOnSide
              />
            </div>
          </div>
        );
      case "1x3":
      case "1x4":
      case "1x5":
        return (
          <div className="flex flex-col gap-4 h-full w-full py-2">
            <CardTitle title={topic.name} />
            <div className="flex flex-col items-center justify-around w-full h-full">
              <LogLevelIndicator level="ERROR" count={ERROR} label="Error" />
              <LogLevelIndicator
                level="WARNING"
                count={WARNING}
                label="Warning"
              />
              <LogLevelIndicator level="INFO" count={INFO} label="Info" />
              <LogLevelIndicator level="DEBUG" count={DEBUG} label="Debug" />
            </div>
          </div>
        );
      case "2x1":
      case "3x1":
      case "4x1":
        return (
          <div className="flex items-center w-full h-full">
            <div className="flex space-x-1 justify-around w-full">
              <LogLevelIndicator level="ERROR" count={ERROR} label="Errs" />
              <LogLevelIndicator level="WARNING" count={WARNING} label="Warn" />
              <LogLevelIndicator level="INFO" count={INFO} label="Info" />
              <LogLevelIndicator level="DEBUG" count={DEBUG} label="Debug" />
            </div>
          </div>
        );
      case "2x2":
        return (
          <div className="flex flex-col justify-around h-full w-full p-1">
            <CardTitle title={topic.name} />
            <div className="flex flex-col justify-around h-full">
              <div className="flex flex-row justify-around w-full">
                <LogLevelIndicator level="ERROR" count={ERROR} label="Errs" />
                <LogLevelIndicator
                  level="WARNING"
                  count={WARNING}
                  label="Warn"
                />
              </div>
              <div className="flex flex-row justify-around">
                <LogLevelIndicator level="INFO" count={INFO} label="Info" />
                <LogLevelIndicator level="DEBUG" count={DEBUG} label="Debug" />
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="flex flex-col justify-between w-full h-full">
            <CardTitle title={topic.name} />
            <div className="flex space-x-2 justify-around w-full">
              <LogLevelIndicator level="ERROR" count={ERROR} label="Errors" />
              <LogLevelIndicator
                level="WARNING"
                count={WARNING}
                label="Warnings"
              />
              <LogLevelIndicator level="INFO" count={INFO} label="Info" />
              <LogLevelIndicator level="DEBUG" count={DEBUG} label="Debug" />
            </div>
            <div>
              {safeMessages
                .slice(0, h <= 3 ? h : h + 2 * (h - 3))
                .map((msg, index) => (
                  <p
                    key={index}
                    className="rounded p-2 text-sm text-gray-700 truncate shadow-sm"
                    title={msg.content} // optional: full message on hover
                  >
                    {msg.content}
                  </p>
                ))}
            </div>

            <CardLastMessageTimedelta lastMessage={safeMessages[0]} />
          </div>
        );
    }
  };

  return (
    <div className="m-auto shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col bg-gray-50">
      {renderContent()}
    </div>
  );
});

LogCard.variants = ["default", "timeline", "grouped"];
LogCard.variantParameters = {
  default: {},
  timeline: {},
  grouped: {},
};

LogCard.propTypes = {
  card: PropTypes.object.isRequired,
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
};

export default LogCard;
